import React, { useState, useEffect, useRef, lazy, Suspense, ReactNode } from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect, useParams, useLocation, useHistory, RouteProps } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { AuthProvider, useAuth } from './components/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ChatProvider } from './contexts/ChatContext';
import { Navigation } from './components/Navigation';
import { SideMenu } from './components/SideMenu';
import { BottomNavigation } from './components/BottomNavigation';
import { LoadingScreen } from './components/LoadingScreen';
import { initDatabase } from './lib/initDatabase';
import { GoogleMapsProvider } from './components/GoogleMapsProvider';
import { supabase } from './lib/db';
import { UserProfilePreview } from './components/UserProfilePreview';
import { ReferralService } from './services/ReferralService';
import { useDeepLinkListener } from './hooks/useDeepLinkListener';
import { PreviewShare } from './pages/PreviewShare';
import { PreviewSharePage } from './pages/PreviewSharePage';
import { HelmetProvider } from 'react-helmet-async';
import { BookingFlow } from './pages/BookingFlow';
import { NotificationProvider } from './contexts/NotificationContext';
import { NotificationBell } from './components/NotificationBell';
import NotificationsPage from './pages/NotificationsPage';
import AppointmentsPage from './pages/AppointmentsPage';
import { notificationService } from './services/NotificationService';

// TikTok-style feed page
const FeedPage = lazy(() => import('./tiktok/FeedPage').then(module => ({ default: module.FeedPage })));

// Dynamically imported components (classic/legacy pages)
const Home = lazy(() => import('./pages/Home').then(module => ({ default: module.Home })));
const Login = lazy(() => import('./pages/Login').then(module => ({ default: module.Login })));
const Signup = lazy(() => import('./pages/Signup').then(module => ({ default: module.Signup })));
const Profile = lazy(() => import('./pages/Profile').then(module => ({ default: module.Profile })));
const Providers = lazy(() => import('./pages/Providers').then(module => ({ default: module.Providers })));
const Contact = lazy(() => import('./pages/Contact').then(module => ({ default: module.Contact })));
const Help = lazy(() => import('./pages/Help').then(module => ({ default: module.Help })));
const FAQ = lazy(() => import('./pages/FAQ').then(module => ({ default: module.FAQ })));
const Privacy = lazy(() => import('./pages/Privacy').then(module => ({ default: module.Privacy })));
const Terms = lazy(() => import('./pages/Terms').then(module => ({ default: module.Terms })));
const Cookies = lazy(() => import('./pages/Cookies').then(module => ({ default: module.Cookies })));
const Marketplace = lazy(() => import('./pages/Marketplace').then(module => ({ default: module.Marketplace })));
const ProviderDetails = lazy(() => import('./pages/ProviderDetails').then(module => ({ default: module.ProviderDetails })));
const SellerDetails = lazy(() => import('./pages/SellerDetails').then(module => ({ default: module.SellerDetails })));
const ChatLayout = lazy(() => import('./components/chat/ChatLayout').then(module => ({ default: module.ChatLayout })));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const SavedItemsPage = lazy(() => import('./components/SavedItemsPage'));
const Orders = lazy(() => import('./components/Orders').then(module => ({ default: module.Orders })));
const ItemDetailsFullPage = lazy(() => import('./components/ItemDetailsFullPage').then(module => ({ default: module.ItemDetailsFullPage })));
const SendersTravelers = lazy(() => import('./pages/SendersTravelers').then(module => ({ default: module.SendersTravelers })));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));

// --- Define Props Interfaces ---
interface CustomRouteProps extends RouteProps {
  children: ReactNode;
}
// ---

// React Router v5 protected route pattern
const ProtectedRoute = ({ children, ...rest }: CustomRouteProps) => {
  const { user } = useAuth();
  return (
    <Route
      {...rest}
      render={({ location }) =>
        user ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: "/login",
              state: { from: location }
            }}
          />
        )
      }
    />
  );
};

const AuthRoute = ({ children, ...rest }: CustomRouteProps) => {
  const { user } = useAuth();
  return (
    <Route
      {...rest}
      render={({ location }) =>
        user ? (
          <Redirect
            to={{
              pathname: "/profile",
              state: { from: location }
            }}
          />
        ) : (
          children
        )
      }
    />
  );
};

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Listen for custom event from FeedTopBar to open the SideMenu
  useEffect(() => {
    const handleOpenSideMenu = () => setIsMenuOpen(true);
    window.addEventListener('open-side-menu', handleOpenSideMenu);
    return () => window.removeEventListener('open-side-menu', handleOpenSideMenu);
  }, []);
  const [showTopNav, setShowTopNav] = useState(true);
  const [showBottomNav, setShowBottomNav] = useState(true);
  const lastScrollY = useRef(0);
  const lastDirection = useRef('up');
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const topNavHeight = 60;
  const bottomNavHeight = 60;
  
  // Get current location to determine page type
  const location = useLocation();
  const isFeedPage = location.pathname === '/';
  const isChatPage = location.pathname.startsWith('/chat');
  // Hide standard navigation on immersive pages (feed) and chat pages
  const isImmersive = isFeedPage;
  const hideNavigation = isImmersive || isChatPage;

  useEffect(() => {
    // Skip scroll handler setup on immersive pages
    if (isImmersive) return;
    
    const mainContent = mainRef.current;
    if (!mainContent) return;

    const handleScroll = () => {
      const currentScrollY = mainContent.scrollTop;
      const direction = currentScrollY > lastScrollY.current ? 'down' : 'up';
      lastScrollY.current = currentScrollY;
      lastDirection.current = direction;

      const scrollHeight = mainContent.scrollHeight;
      const clientHeight = mainContent.clientHeight;
      const isAtBottom = scrollHeight - clientHeight - currentScrollY < 5;

      // Don't hide nav at very top or if at bottom
      if (direction === 'down' && currentScrollY > 20 && !isAtBottom) {
        setShowTopNav(false);
        setShowBottomNav(false);
      } else if (direction === 'up') {
        setShowTopNav(true);
        setShowBottomNav(true);
      }

      // Clear previous timeout
      if (timeoutId.current) clearTimeout(timeoutId.current);

      // Handle scroll end detection
      timeoutId.current = setTimeout(() => {
        const newScrollY = mainContent.scrollTop;
        const newIsAtBottom = scrollHeight - clientHeight - newScrollY < 5;

        // Show bottom nav if scrolling down reached bottom
        if (lastDirection.current === 'down' && newIsAtBottom) {
          setShowBottomNav(true);
        }
      }, 150);
    };

    mainContent.addEventListener('scroll', handleScroll);
    return () => {
      mainContent.removeEventListener('scroll', handleScroll);
      if (timeoutId.current) clearTimeout(timeoutId.current);
    };
  }, [isImmersive]);

  // Render with conditional navigation based on page type
  return (
    <div className="app-theme flex flex-col h-screen overflow-hidden relative">
      {/* Top Navigation - Hidden on immersive pages (feed, onboarding) and chat */}
      {!hideNavigation && (
        <div
          className="fixed top-0 left-0 w-full z-[1000] transition-transform duration-300 ease-in-out"
          style={{
            height: `${topNavHeight}px`,
            transform: showTopNav ? 'translateY(0)' : `translateY(-${topNavHeight}px)`
          }}
        >
          <Navigation onMenuClick={() => setIsMenuOpen(true)} />
          <div className="filter-bar"></div>
        </div>
      )}

      {/* Side Menu */}
      <SideMenu isOpen={isMenuOpen} setIsOpen={setIsMenuOpen} />

      {/* Main Content - No padding on immersive pages */}
      <main
        ref={mainRef}
        className={(isImmersive || isChatPage) ? 'flex-grow overflow-hidden' : 'flex-grow overflow-y-auto'}
        style={(isImmersive || isChatPage) ? undefined : {
          paddingTop: `${topNavHeight}px`,
          paddingBottom: `${bottomNavHeight}px`
        }}
      >
        {children}
      </main>

      {/* Bottom Navigation - Hidden on immersive/chat pages */}
      {!hideNavigation && (
        <div
          className="fixed bottom-0 left-0 w-full z-[1000] transition-transform duration-300 ease-in-out"
          style={{
            height: `${bottomNavHeight}px`,
            transform: showBottomNav ? 'translateY(0)' : `translateY(${bottomNavHeight}px)`
          }}
        >
          <BottomNavigation />
        </div>
      )}
    </div>
  );
};

// Use React Router v5 syntax for this component
const UserProfilePreviewWrapper = () => {
  const { userId } = useParams<{ userId?: string }>();
  const location = useLocation();
  const isPreview = new URLSearchParams(location.search).get('preview') === 'true';
  const { user } = useAuth();
  
  // If it's a preview, use the URL parameter regardless of logged-in user
  // Otherwise, check if showing own profile
  const profileIdToUse = isPreview ? userId : (user?.id || undefined);
  
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('id, contact_name, email, phone, bio, image_url, role')
        .eq('id', profileIdToUse)
        .single();
      if (error) {
        console.error('Error fetching profile:', error);
      } else {
        setProfile(data);
      }
      setLoading(false);
    };

    if (profileIdToUse) {
      fetchProfile();
    }
  }, [profileIdToUse]);

  if (loading) {
    return <p>Loading profile...</p>;
  }

  if (!profile) {
    return <p>Profile not found.</p>;
  }

  return <UserProfilePreview profile={profile} />;
};

// DeepLinkListener to handle product and referral deep links
const DeepLinkListener = () => {
  useDeepLinkListener();
  return null;
};

// Create a separate component for the home route logic (now uses TikTok-style feed)
const HomeRoute = () => {
  return <FeedPage />;
};

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
 
  useEffect(() => {
    const init = async () => {
      try {
        const success = await initDatabase();
        if (!success) setError('Failed to initialize application');
        
        // Initialize the ReferralService (which handles Branch SDK)
        await ReferralService.getInstance().initialize();

      } catch (err: any) {
        console.error('Initialization error:', err);
        setError('Failed to initialize application');
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  if (isLoading) return <LoadingScreen />;
  if (error) return <div>Error: {error}</div>;

  return (
    <HelmetProvider>
      <Router>
        <GoogleMapsProvider>
          <LanguageProvider>
            <ThemeProvider>
            <AuthProvider>
              <NotificationProvider>
              <ChatProvider>
                <Layout>
                  <Suspense fallback={<LoadingScreen />}>
                    <Switch>
                      {/* Auth routes */}
                      <AuthRoute path="/login">
                        <Login />
                      </AuthRoute>
                      <AuthRoute path="/signup">
                        <Signup />
                      </AuthRoute>
                      <Route exact path="/forgot-password" component={ForgotPassword} />
                      <Route exact path="/reset-password" component={ResetPassword} />
                      
                        {/* Home route - TikTok-style feed */}
                        <Route exact path="/" component={HomeRoute} />
                          <Route exact path="/app">
                            <Redirect to="/" />
                          </Route>

                        {/* Classic home page (legacy) */}
                        <Route exact path="/home-classic" component={Home} />

                      {/* Other routes */}
                      <Route exact path="/providers" component={Providers} />
                      <Route exact path="/services">
                        <Redirect to="/providers" />
                      </Route>
                      <Route exact path="/provider/:id" component={ProviderDetails} />
                      <Route exact path="/seller/:id" component={SellerDetails} />
                      <Route exact path="/marketplace" component={Marketplace} />
                      <Route exact path="/shop">
                        <Redirect to="/marketplace" />
                      </Route>
                      
                      <ProtectedRoute exact path="/profile">
                        <Profile />
                      </ProtectedRoute>
                      <ProtectedRoute exact path="/me">
                        <Redirect to="/profile" />
                      </ProtectedRoute>
                      
                      <Route exact path="/contact" component={Contact} />
                      <Route exact path="/help" component={Help} />
                      <Route exact path="/faq" component={FAQ} />
                      <Route exact path="/privacy" component={Privacy} />
                      <Route exact path="/terms" component={Terms} />
                      <Route exact path="/cookies" component={Cookies} />
                      
                        <ProtectedRoute path="/chat">
                        <ChatLayout />
                      </ProtectedRoute>
                        <ProtectedRoute exact path="/inbox">
                          <Redirect to="/chat" />
                        </ProtectedRoute>
                      
                      <Route exact path="/saved-items" component={SavedItemsPage} />
                      <Route exact path="/saved">
                        <Redirect to="/saved-items" />
                      </Route>
                      <ProtectedRoute exact path="/orders">
                        <Orders />
                      </ProtectedRoute>
                      <ProtectedRoute exact path="/cart">
                        <Redirect to="/orders" />
                      </ProtectedRoute>
                      <Route exact path="/profile/:userId" component={UserProfilePreviewWrapper} />
                      <Route exact path="/itemDetails/:id" component={ItemDetailsFullPage} />
                      <Route exact path="/senders-travelers" component={SendersTravelers} />
                      <Route exact path="/preview-share" component={PreviewShare} />
                      <Route exact path="/preview-share/:item_id" component={PreviewSharePage} />
                          <Route path="/providers/:provider_id/book" component={BookingFlow} />
                          <Route path="/book/:provider_id" component={BookingFlow} />
                          <ProtectedRoute exact path="/notifications">
                            <NotificationsPage />
                          </ProtectedRoute>
                          <ProtectedRoute path="/appointments">
                            <AppointmentsPage />
                          </ProtectedRoute>
                          <ProtectedRoute exact path="/bookings">
                            <Redirect to="/appointments" />
                          </ProtectedRoute>

                      {/* Catch all route - redirect to home */}
                      <Route path="*">
                        <Redirect to="/" />
                      </Route>
                    </Switch>
                  </Suspense>
                </Layout>
                <Toaster position="bottom-center" />
              </ChatProvider>
              </NotificationProvider>
            </AuthProvider>
            </ThemeProvider>
          </LanguageProvider>
        </GoogleMapsProvider>
      </Router>
    </HelmetProvider>
  );
};

export default App;